import type { HTTPService } from "@soft-db/http";
import type { Jsonify } from "type-fest";
import type {
	ApiCommentQuery,
	ApiDataQueryOptions,
	ApiEventFileQuery,
	ApiEventQuery,
	ApiEventWaveformQuery,
	Comment,
	CreateComment,
	DataFormat,
	DataQuery,
	GraphRecordResponse,
	JSONRecordResponse,
	RawRecordResponse,
	StationEvent,
	StationEventFile,
	WaveformRecordResponse,
} from "../type";

type ApiDataResponseFromFormat<T extends DataFormat> = T extends "json"
	? JSONRecordResponse[]
	: T extends "graph"
		? GraphRecordResponse[]
		: T extends "raw"
			? RawRecordResponse[]
			: T extends "csv"
				? string
				: never;

type ApiEventsResponseFromFormat<T extends DataFormat> = T extends "raw"
	? Jsonify<StationEvent[]>
	: T extends "csv"
		? string
		: never;

type ApiEventWaveformResponseFromFormat<
	T extends ApiEventWaveformQuery,
	F extends DataFormat,
> = F extends "raw" ? WaveformRecordResponse<T> : F extends "csv" ? string : never;

export class ArchivistApi {
	constructor(private _http: HTTPService) {}

	async fetchData<T extends DataFormat>(
		site: string,
		params: ApiDataQueryOptions,
		queries: DataQuery[],
		format: T
	) {
		params = { ...params, format };

		return await this._http.post<ApiDataResponseFromFormat<T>>(
			`/inquiry/sites/${site}/records`,
			{
				queries,
			},
			{
				params,
				// Timeout of 5 min
				timeout: 5 * 60 * 1000,
				version: 1,
			}
		);
	}

	async fetchJSONData(site: string, params: ApiDataQueryOptions, queries: DataQuery[]) {
		return this.fetchData(site, params, queries, "json");
	}

	async fetchCSVData(site: string, params: ApiDataQueryOptions, queries: DataQuery[]) {
		return this.fetchData(site, params, queries, "csv");
	}

	async fetchEvents<T extends DataFormat>(siteId: string, query: ApiEventQuery, format: T) {
		return await this._http.get<ApiEventsResponseFromFormat<T>>(`/inquiry/sites/${siteId}/events`, {
			params: { ...query, format },
			version: 1,
		});
	}

	async fetchCSVEvents(siteId: string, query: ApiEventQuery) {
		return this.fetchEvents(siteId, query, "csv");
	}

	async fetchEvent(siteId: string, eventId: string): Promise<Jsonify<StationEvent> | undefined> {
		return await this._http.get(`/inquiry/sites/${siteId}/events/${eventId}`, { version: 1 });
	}

	async fetchEventFiles(siteId: string, eventId: string, query: ApiEventFileQuery) {
		return await this._http.get<Jsonify<StationEventFile[]>>(
			`/inquiry/sites/${siteId}/events/${eventId}/files`,
			{
				params: query,
				version: 1,
			}
		);
	}

	async fetchEventWaveform<T extends ApiEventWaveformQuery, F extends DataFormat>(
		siteId: string,
		eventId: string,
		query: T,
		format: F
	) {
		return await this._http.get<ApiEventWaveformResponseFromFormat<T, F>>(
			`/inquiry/sites/${siteId}/events/${eventId}/waveform`,
			{
				params: { ...query, format },
				version: 1,
			}
		);
	}

	async fetchCSVEventWaveform<T extends ApiEventWaveformQuery>(
		siteId: string,
		eventId: string,
		query: T
	) {
		return this.fetchEventWaveform(siteId, eventId, query, "csv");
	}

	async fetchRawEventWaveform<T extends ApiEventWaveformQuery>(
		siteId: string,
		eventId: string,
		query: T
	) {
		return this.fetchEventWaveform(siteId, eventId, query, "raw");
	}

	async fetchComments(siteId: string, query: ApiCommentQuery) {
		return await this._http.get<Jsonify<Comment[]>>(`/inquiry/sites/${siteId}/comments`, {
			params: query,
			version: 1,
		});
	}

	async createComment(siteId: string, comment: CreateComment) {
		return await this._http.post<Comment>(`/inquiry/sites/${siteId}/comments`, comment, {
			version: 1,
		});
	}

	async changeComment(siteId: string, comment: CreateComment) {
		return await this._http.put<Comment>(
			`/inquiry/sites/${siteId}/comments/${comment.id}`,
			comment,
			{ version: 1 }
		);
	}

	async deleteComment(siteId: string, id: string) {
		return await this._http.delete<Comment>(`/inquiry/sites/${siteId}/comments/${id}`, {
			version: 1,
		});
	}
}
